/**
 * 弁当予約通知コンポーネント（強化版）
 * カスタマイズ可能なサウンド通知とリマインダー機能付き
 */

import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Card, Badge, message, notification, Drawer, Select, InputNumber, Switch, List, Space } from 'antd';
import { Printer, Clock, Phone, User, Package, AlertCircle, Settings, Volume2, Bell, CheckCircle } from 'lucide-react';
import api from '../../services/api';

interface BentoOrder {
  id: number;
  orderNumber: string;
  customerName: string;
  phone: string;
  pickupTime: string;
  items: Array<{
    name: string;
    quantity: number;
    price: number;
  }>;
  totalAmount: number;
  quantityTotal: number;
  memo?: string;
  createdAt: string;
  status: string;
}

interface NotificationSettings {
  soundEnabled: boolean;
  soundFile: string;
  soundVolume: number;
  reminderEnabled: boolean;
  reminderInterval: number; // 分
  checkInterval: number; // 秒
}

interface BentoOrderNotificationProps {
  onOrderReceived?: (order: BentoOrder) => void;
  hideSettingsButton?: boolean;
  onSettingsClick?: () => void;
  onUnreadCountChange?: (count: number) => void; // 未読数が変更されたときのコールバック
}

const BentoOrderNotificationEnhanced: React.FC<BentoOrderNotificationProps> = ({
  onOrderReceived,
  hideSettingsButton = false,
  onSettingsClick,
  onUnreadCountChange
}) => {
  const [visible, setVisible] = useState(false);
  const [currentOrder, setCurrentOrder] = useState<BentoOrder | null>(null);

  // 初回チェック時刻を今日の0時に設定（今日の注文を全て検出するため）
  const getStartOfToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  const lastCheckTimeRef = useRef(getStartOfToday());

  // LocalStorageから処理済みIDを復元（画面遷移しても保持）
  const loadProcessedOrders = (): Set<number> => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const stored = localStorage.getItem(`processedBentoOrders_${today}`);
      if (stored) {
        return new Set(JSON.parse(stored));
      }
    } catch (e) {
      console.error('[弁当通知] localStorage読み込みエラー:', e);
    }
    return new Set();
  };

  const processedOrdersRef = useRef<Set<number>>(loadProcessedOrders());
  const [unconfirmedOrders, setUnconfirmedOrders] = useState<Map<number, BentoOrder>>(new Map());
  const [settingsVisible, setSettingsVisible] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const reminderTimerRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  // コンポーネントマウント時のログ
  useEffect(() => {
    console.log('[弁当通知] BentoOrderNotificationEnhanced マウント完了');
    console.log('[弁当通知] 初回チェック基準時刻:', lastCheckTimeRef.current);
    console.log('[弁当通知] 処理済み注文数:', processedOrdersRef.current.size);
    return () => {
      console.log('[弁当通知] BentoOrderNotificationEnhanced アンマウント');
    };
  }, []);

  // 未読数が変更されたら親に通知
  useEffect(() => {
    if (onUnreadCountChange) {
      onUnreadCountChange(unconfirmedOrders.size);
    }
  }, [unconfirmedOrders, onUnreadCountChange]);
  
  // 通知設定（ローカルストレージから読み込み）
  const [settings, setSettings] = useState<NotificationSettings>(() => {
    const saved = localStorage.getItem('bentoNotificationSettings');
    if (saved) {
      return JSON.parse(saved);
    }
    return {
      soundEnabled: true,
      soundFile: '/sounds/chime1.mp3',
      soundVolume: 0.7,
      reminderEnabled: true,
      reminderInterval: 2, // 2分ごと
      checkInterval: 15 // 15秒ごと（適度な間隔）
    };
  });

  // 利用可能なサウンドリスト
  const [availableSounds] = useState([
    { value: '/sounds/chime1.mp3', label: 'チャイム1' },
    { value: '/sounds/chime2.mp3', label: 'チャイム2' },
    { value: '/sounds/bell1.mp3', label: 'ベル1' },
    { value: '/sounds/alert1.mp3', label: 'アラート1' },
    { value: '/sounds/doorbell.mp3', label: 'ドアチャイム' },
    { value: 'custom', label: 'カスタムURL' }
  ]);

  // 設定を保存
  useEffect(() => {
    localStorage.setItem('bentoNotificationSettings', JSON.stringify(settings));
  }, [settings]);

  // AudioContextを初期化（ユーザー操作後）
  useEffect(() => {
    const initAudioContext = () => {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
        console.log('[通知音] AudioContext初期化完了');
      }
      // ユーザー操作後に resume
      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume().then(() => {
          console.log('[通知音] AudioContext再開完了');
        });
      }
    };

    // 画面クリック時にAudioContextを有効化
    document.addEventListener('click', initAudioContext, { once: false });
    document.addEventListener('touchstart', initAudioContext, { once: false });

    return () => {
      document.removeEventListener('click', initAudioContext);
      document.removeEventListener('touchstart', initAudioContext);
    };
  }, []);

  // 新規注文をチェック
  useEffect(() => {
    const checkNewOrders = async () => {
      try {
        console.log('[弁当通知] 注文チェック開始...');
        const response = await api.get('/bento-orders/today');
        console.log('[弁当通知] API応答:', response.data);

        if (response.data.success && response.data.orders) {
          const orders = response.data.orders;
          const now = new Date();
          const currentHour = now.getHours();

          console.log('[弁当通知] 注文件数:', orders.length, '現在時刻:', currentHour + '時');

          // 終日新規注文をチェック（時間制限を削除）
          // if (currentHour >= 10) {
            let newOrderCount = 0;
            orders.forEach((order: any) => {
              const orderTime = new Date(order.created_at);
              const isNotProcessed = !processedOrdersRef.current.has(order.id);
              const isUnconfirmed = !order.is_confirmed;

              console.log(`[弁当通知] 注文ID ${order.id}: 作成時刻=${orderTime.toLocaleString()}, 未処理=${isNotProcessed}, 未確認=${isUnconfirmed}`);

              // 未確認かつ未処理の場合のみ通知
              if (isNotProcessed && isUnconfirmed) {
                newOrderCount++;
                // APIレスポンスのフィールド名を変換（スネークケース → キャメルケース）
                const normalizedOrder = {
                  ...order,
                  orderNumber: order.order_number || order.orderNumber,
                  customerName: order.customer_name || order.customerName,
                  pickupTime: order.pickup_time || order.pickupTime,
                  totalAmount: order.total_amount || order.totalAmount || 0,
                  quantityTotal: order.quantity_total || order.quantityTotal || 0,
                  createdAt: order.created_at || order.createdAt,
                  orderMemo: order.order_memo || order.orderMemo,
                  isConfirmed: order.is_confirmed
                };

                console.log('[弁当通知] 🔔 新規注文検出！', normalizedOrder);

                // 未確認リストに追加
                setUnconfirmedOrders(prev => new Map(prev).set(order.id, normalizedOrder));
                // ポップアップ表示
                showOrderNotification(normalizedOrder);
                // 処理済みIDを追加してlocalStorageに保存
                processedOrdersRef.current.add(order.id);
                const today = new Date().toISOString().split('T')[0];
                localStorage.setItem(`processedBentoOrders_${today}`, JSON.stringify(Array.from(processedOrdersRef.current)));
              }
            });

            console.log('[弁当通知] 新規注文検出数:', newOrderCount);
          // }
        }
        
        lastCheckTimeRef.current = new Date();
      } catch (error) {
        console.error('注文チェックエラー:', error);
      }
    };

    // 初回実行
    checkNewOrders();

    // 設定された間隔でチェック
    const interval = setInterval(checkNewOrders, settings.checkInterval * 1000);
    
    return () => clearInterval(interval);
  }, [settings.checkInterval]); // lastCheckTimeとprocessedOrdersを除外して無限ループを防ぐ

  // リマインダー機能
  useEffect(() => {
    if (settings.reminderEnabled && unconfirmedOrders.size > 0) {
      // 既存のタイマーをクリア
      if (reminderTimerRef.current) {
        clearInterval(reminderTimerRef.current);
      }
      
      // 新しいリマインダータイマーを設定
      reminderTimerRef.current = setInterval(() => {
        if (unconfirmedOrders.size > 0) {
          playNotificationSound(true); // リマインダー音を再生
          
          // 通知も再表示
          notification.warning({
            message: '🔔 未確認の弁当予約があります',
            description: `${unconfirmedOrders.size}件の予約が未確認です。確認してください。`,
            duration: 0,
            placement: 'topRight',
            style: {
              backgroundColor: '#fff3cd',
              border: '2px solid #ffc107'
            }
          });
        }
      }, settings.reminderInterval * 60 * 1000); // 分をミリ秒に変換
    }
    
    return () => {
      if (reminderTimerRef.current) {
        clearInterval(reminderTimerRef.current);
      }
    };
  }, [settings.reminderEnabled, settings.reminderInterval, unconfirmedOrders]);

  // 注文通知を表示
  const showOrderNotification = (order: BentoOrder) => {
    console.log('[弁当通知] ポップアップ表示開始:', order.customerName);
    setCurrentOrder(order);
    setVisible(true);

    // 音を鳴らす
    if (settings.soundEnabled) {
      playNotificationSound();
    }

    console.log('[弁当通知] notification.open呼び出し');
    // システム通知も表示
    notification.open({
      message: '🍱 新しい弁当予約',
      description: `${order.customerName}様 ${order.pickupTime}受取 ${order.quantityTotal}個`,
      duration: 0,
      placement: 'topRight',
      style: {
        backgroundColor: '#fff3cd',
        border: '2px solid #ffc107'
      },
      btn: (
        <Button type="primary" size="small" onClick={() => handleConfirmOrder(order.id)}>
          確認
        </Button>
      )
    });
    
    // コールバック実行
    if (onOrderReceived) {
      onOrderReceived(order);
    }
  };

  // 通知音を再生
  const playNotificationSound = (isReminder = false) => {
    console.log('[通知音] 再生開始 - soundEnabled:', settings.soundEnabled, 'volume:', settings.soundVolume);

    // Web Audio APIを使ってビープ音を再生
    try {
      // AudioContextを初期化（まだの場合）
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
        console.log('[通知音] AudioContext作成');
      }

      const context = audioContextRef.current;

      // suspended状態の場合は resume
      if (context.state === 'suspended') {
        context.resume().then(() => {
          console.log('[通知音] AudioContext resume完了');
          playBeep(context, isReminder);
        }).catch(err => {
          console.error('[通知音] AudioContext resume失敗:', err);
        });
      } else {
        playBeep(context, isReminder);
      }
    } catch (error) {
      console.error('[通知音] Web Audio API エラー:', error);
      // フォールバック：alert（最終手段）
      if (isReminder) {
        message.warning('⏰ リマインダー：未確認の予約があります');
      }
    }
  };

  // ビープ音を再生する内部関数
  const playBeep = (context: AudioContext, isReminder: boolean) => {
    const oscillator = context.createOscillator();
    const gainNode = context.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(context.destination);

    // 周波数とパターン設定
    oscillator.frequency.value = isReminder ? 880 : 660;
    oscillator.type = 'sine';

    // 音量設定
    const volume = settings.soundVolume * 0.3; // 0.3倍で適度な音量に
    gainNode.gain.setValueAtTime(volume, context.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, context.currentTime + 0.2);

    oscillator.start(context.currentTime);
    oscillator.stop(context.currentTime + 0.2);

    console.log('[通知音] ビープ音再生完了');

    // リマインダーの場合は3回繰り返す
    if (isReminder) {
      setTimeout(() => {
        const osc2 = context.createOscillator();
        const gain2 = context.createGain();
        osc2.connect(gain2);
        gain2.connect(context.destination);
        osc2.frequency.value = 880;
        osc2.type = 'sine';
        gain2.gain.setValueAtTime(volume, context.currentTime);
        gain2.gain.exponentialRampToValueAtTime(0.01, context.currentTime + 0.2);
        osc2.start(context.currentTime);
        osc2.stop(context.currentTime + 0.2);
        console.log('[通知音] リマインダー2回目');
      }, 300);

      setTimeout(() => {
        const osc3 = context.createOscillator();
        const gain3 = context.createGain();
        osc3.connect(gain3);
        gain3.connect(context.destination);
        osc3.frequency.value = 880;
        osc3.type = 'sine';
        gain3.gain.setValueAtTime(volume, context.currentTime);
        gain3.gain.exponentialRampToValueAtTime(0.01, context.currentTime + 0.2);
        osc3.start(context.currentTime);
        osc3.stop(context.currentTime + 0.2);
        console.log('[通知音] リマインダー3回目');
      }, 600);
    }
  };

  // 注文を確認済みにする
  const handleConfirmOrder = async (orderId: number) => {
    console.log('[弁当通知] 注文確認:', orderId);

    // 未確認リストから削除
    setUnconfirmedOrders(prev => {
      const newMap = new Map(prev);
      newMap.delete(orderId);
      return newMap;
    });

    // 処理済みIDに追加してlocalStorageに保存
    processedOrdersRef.current.add(orderId);
    const today = new Date().toISOString().split('T')[0];
    const processed = Array.from(processedOrdersRef.current);
    localStorage.setItem(`processedBentoOrders_${today}`, JSON.stringify(processed));
    console.log('[弁当通知] 処理済みID保存:', processed);

    // リマインダーを停止（未確認がゼロになった場合）
    if (unconfirmedOrders.size === 1) { // 削除前なので1
      if (reminderTimerRef.current) {
        clearInterval(reminderTimerRef.current);
        reminderTimerRef.current = null;
      }
    }
  };

  // 予約票を印刷
  const handlePrint = async () => {
    if (!currentOrder) return;
    
    try {
      const response = await api.get(`/bento-orders/print/order/${currentOrder.id}`);
      
      if (response.data.success) {
        // 新しいウィンドウで印刷
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(response.data.html);
          printWindow.document.close();
          printWindow.onafterprint = () => {
            printWindow.close();
          };
        }
        
        message.success('予約票を印刷しました');
        
        // ステータスを調理中に自動更新（印刷＝調理開始）
        await api.put(`/bento-orders/${currentOrder.id}/status`, {
          status: 'preparing'
        });

        message.success('調理を開始しました');

        // 確認済みとして処理
        handleConfirmOrder(currentOrder.id);

        // ポップアップを自動で閉じる
        setVisible(false);
      }
    } catch (error) {
      console.error('印刷エラー:', error);
      message.error('印刷に失敗しました');
    }
  };

  // サウンドテスト
  const testSound = () => {
    playNotificationSound();
  };

  return (
    <>
      {/* ポップアップモーダル */}
      <Modal
        title={
          <div style={{ 
            fontSize: '20px', 
            fontWeight: 'bold',
            color: '#ff6b35'
          }}>
            <AlertCircle style={{ display: 'inline', marginRight: '8px' }} />
            🍱 新しい弁当予約が入りました！
          </div>
        }
        open={visible}
        onCancel={() => setVisible(false)}
        width={600}
        footer={[
          <Button
            key="print"
            type="primary"
            icon={<Printer />}
            onClick={handlePrint}
            size="large"
            style={{
              backgroundColor: '#52c41a',
              fontSize: '18px',
              padding: '8px 32px',
              height: 'auto',
              borderColor: '#52c41a'
            }}
          >
            🖨️ 印刷して調理開始
          </Button>,
          <Button
            key="later"
            onClick={() => setVisible(false)}
            size="small"
            style={{ fontSize: '12px' }}
          >
            後で確認
          </Button>
        ]}
      >
        {currentOrder && (
          <Card style={{ backgroundColor: '#fffbe6' }}>
            {/* 既存の注文詳細表示 */}
            <div style={{ fontSize: '18px', marginBottom: '16px' }}>
              <Badge 
                status="processing" 
                text={`注文番号: ${currentOrder.orderNumber}`} 
                style={{ fontSize: '16px' }}
              />
            </div>
            
            <div style={{ display: 'grid', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', fontSize: '16px' }}>
                <Clock style={{ marginRight: '8px', color: '#ff6b35' }} />
                <strong>受取時間: {currentOrder.pickupTime}</strong>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <User style={{ marginRight: '8px', color: '#1890ff' }} />
                {currentOrder.customerName} 様
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Phone style={{ marginRight: '8px', color: '#52c41a' }} />
                {currentOrder.phone}
              </div>
              
              <div style={{ 
                backgroundColor: '#fff', 
                padding: '12px', 
                borderRadius: '4px',
                border: '1px solid #d9d9d9'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                  <Package style={{ marginRight: '8px' }} />
                  <strong>注文内容:</strong>
                </div>
                {currentOrder.items.map((item, index) => (
                  <div key={index} style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    padding: '4px 0'
                  }}>
                    <span>{item.name}</span>
                    <span style={{ fontWeight: 'bold' }}>×{item.quantity}</span>
                  </div>
                ))}
              </div>
              
              <div style={{ 
                textAlign: 'center', 
                fontSize: '20px', 
                fontWeight: 'bold',
                padding: '12px',
                backgroundColor: '#fff',
                border: '2px solid #ff6b35',
                borderRadius: '4px'
              }}>
                合計: {currentOrder.quantityTotal}個 / ¥{currentOrder.totalAmount.toLocaleString()}
              </div>
            </div>
          </Card>
        )}
      </Modal>

      {/* 設定ボタン（条件付き表示） */}
      {!hideSettingsButton && (
        <Button
          icon={<Settings />}
          onClick={() => {
            if (onSettingsClick) {
              onSettingsClick();
            } else {
              setSettingsVisible(true);
            }
          }}
          style={{
            position: 'fixed',
            top: '120px',
            right: '20px',
            zIndex: 999
          }}
        >
          通知設定
        </Button>
      )}

      {/* 未確認注文バッジ */}
      {unconfirmedOrders.size > 0 && (
        <Badge 
          count={unconfirmedOrders.size}
          style={{
            position: 'fixed',
            top: '80px',
            right: '30px',
            zIndex: 1000
          }}
        >
          <Bell size={32} color="#ff6b35" />
        </Badge>
      )}

      {/* 設定ドロワー */}
      <Drawer
        title="弁当予約通知設定"
        placement="right"
        onClose={() => setSettingsVisible(false)}
        open={settingsVisible}
        width={400}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* サウンド設定 */}
          <Card title="サウンド設定" size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>サウンド通知</span>
                <Switch
                  checked={settings.soundEnabled}
                  onChange={(checked) => setSettings({...settings, soundEnabled: checked})}
                />
              </div>
              
              <div>
                <label>通知音:</label>
                <Select
                  style={{ width: '100%', marginTop: '8px' }}
                  value={settings.soundFile}
                  onChange={(value) => setSettings({...settings, soundFile: value})}
                  disabled={!settings.soundEnabled}
                >
                  {availableSounds.map(sound => (
                    <Select.Option key={sound.value} value={sound.value}>
                      {sound.label}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              
              <div>
                <label>音量: {Math.round(settings.soundVolume * 100)}%</label>
                <InputNumber
                  min={0}
                  max={1}
                  step={0.1}
                  value={settings.soundVolume}
                  onChange={(value) => setSettings({...settings, soundVolume: value || 0.5})}
                  style={{ width: '100%', marginTop: '8px' }}
                  disabled={!settings.soundEnabled}
                />
              </div>
              
              <Button 
                icon={<Volume2 />} 
                onClick={testSound}
                disabled={!settings.soundEnabled}
              >
                サウンドテスト
              </Button>
            </Space>
          </Card>

          {/* リマインダー設定 */}
          <Card title="リマインダー設定" size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>リマインダー機能</span>
                <Switch
                  checked={settings.reminderEnabled}
                  onChange={(checked) => setSettings({...settings, reminderEnabled: checked})}
                />
              </div>
              
              <div>
                <label>リマインダー間隔（分）:</label>
                <InputNumber
                  min={1}
                  max={10}
                  value={settings.reminderInterval}
                  onChange={(value) => setSettings({...settings, reminderInterval: value || 2})}
                  style={{ width: '100%', marginTop: '8px' }}
                  disabled={!settings.reminderEnabled}
                />
              </div>
              
              <div>
                <label>新規注文チェック間隔（秒）:</label>
                <InputNumber
                  min={5}
                  max={60}
                  value={settings.checkInterval}
                  onChange={(value) => setSettings({...settings, checkInterval: value || 10})}
                  style={{ width: '100%', marginTop: '8px' }}
                />
              </div>
            </Space>
          </Card>

          {/* 未確認注文リスト */}
          {unconfirmedOrders.size > 0 && (
            <Card title={`未確認注文 (${unconfirmedOrders.size}件)`} size="small">
              <List
                dataSource={Array.from(unconfirmedOrders.values())}
                renderItem={order => (
                  <List.Item
                    actions={[
                      <Button 
                        type="link" 
                        icon={<CheckCircle />}
                        onClick={() => handleConfirmOrder(order.id)}
                      >
                        確認
                      </Button>
                    ]}
                  >
                    <List.Item.Meta
                      title={`${order.customerName}様 - ${order.pickupTime}`}
                      description={`${order.quantityTotal}個 / ¥${order.totalAmount.toLocaleString()}`}
                    />
                  </List.Item>
                )}
              />
            </Card>
          )}
        </Space>
      </Drawer>
    </>
  );
};

export default BentoOrderNotificationEnhanced;